import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as dmsService from "../../../services/dmsService";
import * as finService from "../../../services/finService";
import useFinOptions, { optionByCode } from "../../Fin/useFinOptions";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, act, hint, linkBtn } from "../../Fin/gl/glUi";
import { ActionHistory, DealEditor, DmsBadge, DocHead, Panel, Section, Totals, dealPayload, emptyDeal, run, useDealFromDoc } from "./dmsUi";
import { AppDropdown, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, Tabs, formatDate, formatDateTime, inputStyle, pageStyle, todayYmd, useLoad } from "../../../components/ui";

// Vehicle quotations (Dealership → Quotations): the client manual's quotation screen with its
// actions - Submit, Send to customer, Follow up, Extend validity, Revise, Accept (creates the
// sales order), Lost, Cancel, customer / bank PDF - plus the prospects being quoted.
const STATUS = [
  ["", "All open"],
  ["DRAFT", "Draft"],
  ["PENDING_APPROVAL", "Pending approval"],
  ["APPROVED", "Approved"],
  ["PENDING_WITH_CUSTOMER", "With customer"],
  ["WON", "Won"],
  ["LOST", "Lost"],
  ["EXPIRED", "Expired"],
  ["CANCELLED", "Cancelled"],
].map(([value, label]) => ({ value, label }));

function useLookup(type) {
  const { data } = useLoad(() => dmsService.listLookups({ lookup_type: type, status: "ACTIVE" }), [type]);
  return (data || []).map((r) => ({ label: r.name, value: r.code }));
}

/* ------------------------------ editor ------------------------------ */

function PartyPicker({ value, onChange, disabled }) {
  const customers = useLoad(() => finService.listCustomers({ size: 200 }), []);
  const prospects = useLoad(() => dmsService.listProspects({ status: "OPEN", size: 200 }), []);
  const mode = value.mode || "new";
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div>
      <div className="d-flex gap-3 mb-2" style={{ fontSize: 14 }}>
        {[
          ["new", "New prospect"],
          ["prospect", "Existing prospect"],
          ["customer", "Existing customer"],
        ].map(([k, l]) => (
          <label key={k}>
            <input type="radio" name="party" checked={mode === k} disabled={disabled} onChange={() => set({ mode: k })} /> {l}
          </label>
        ))}
      </div>
      {mode === "customer" ? (
        <AppDropdown id="q-cust" value={value.customer_account_id || null} filter placeholder="Customer" disabled={disabled} options={(customers.data || []).map((c) => ({ label: `${c.name} (${c.account_number}) ${c.phone || ""}`, value: c.id }))} onChange={(e) => set({ customer_account_id: e.value })} />
      ) : null}
      {mode === "prospect" && value.prospect_id && !(prospects.data || []).some((p) => p.id === value.prospect_id) ? <div style={hint}>The prospect from the enquiry (or choose another below).</div> : null}
      {mode === "prospect" ? (
        <AppDropdown id="q-pros" value={value.prospect_id || null} filter placeholder="Prospect" disabled={disabled} options={(prospects.data || []).map((p) => ({ label: `${p.display_name} ${p.phone}`, value: p.id }))} onChange={(e) => set({ prospect_id: e.value })} />
      ) : null}
      {mode === "new" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0 12px" }}>
          <Field label="Type" htmlFor="np-type">
            <select id="np-type" className="form-select" value={value.party_type || "INDIVIDUAL"} disabled={disabled} onChange={(e) => set({ party_type: e.target.value })}>
              <option value="INDIVIDUAL">Individual</option>
              <option value="COMPANY">Company</option>
            </select>
          </Field>
          {value.party_type === "COMPANY" ? (
            <Field label="Company name" required htmlFor="np-co">
              <input id="np-co" className="form-control" style={inputStyle} value={value.company_name || ""} disabled={disabled} onChange={(e) => set({ company_name: e.target.value })} />
            </Field>
          ) : (
            <>
              <Field label="First name" required htmlFor="np-fn">
                <input id="np-fn" className="form-control" style={inputStyle} value={value.first_name || ""} disabled={disabled} onChange={(e) => set({ first_name: e.target.value })} />
              </Field>
              <Field label="Last name" htmlFor="np-ln">
                <input id="np-ln" className="form-control" style={inputStyle} value={value.last_name || ""} disabled={disabled} onChange={(e) => set({ last_name: e.target.value })} />
              </Field>
            </>
          )}
          <Field label="Phone" required htmlFor="np-ph">
            <input id="np-ph" className="form-control" style={inputStyle} value={value.phone || ""} disabled={disabled} onChange={(e) => set({ phone: e.target.value })} />
          </Field>
          <Field label="Email" htmlFor="np-em">
            <input id="np-em" className="form-control" style={inputStyle} value={value.email || ""} disabled={disabled} onChange={(e) => set({ email: e.target.value })} />
          </Field>
        </div>
      ) : null}
    </div>
  );
}

const partyPayload = (p) =>
  p.mode === "customer"
    ? { customer_account_id: p.customer_account_id }
    : p.mode === "prospect"
      ? { prospect_id: p.prospect_id }
      : { prospect: { party_type: p.party_type || "INDIVIDUAL", first_name: p.first_name, last_name: p.last_name, company_name: p.company_name, phone: p.phone, email: p.email, lead_id: p.lead_id || undefined } };

function QuoteEditor({ quote, preset, onHide, onSaved }) {
  const fin = useFinOptions(["locations"]);
  const team = useDmsOptions(["salespersons", "price-lists"]);
  const dealOpts = useDmsOptions(["variants", "colours:ext", "colours:int", "charge-types"], 0, { ext: { colour_type: "EXTERIOR" }, int: { colour_type: "INTERIOR" } });
  const payTypes = useLookup("PAYMENT_TYPE");
  const banks = useLookup("BANK");
  const fromDoc = useDealFromDoc(quote, dealOpts);
  const [head, setHead] = useState(null);
  const [party, setParty] = useState({ mode: "new", ...(preset && preset.party) });
  const [deal, setDeal] = useState(emptyDeal());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (quote && fromDoc && !head) {
      setDeal(fromDoc);
      setHead({ location_id: optionByCode(fin.locations, quote.location_code), salesperson_employee_id: null, customer_type: quote.customer_type, payment_type: quote.payment_type, contact_name: quote.contact_name || "", remarks: quote.remarks || "", price_list_id: optionByCode(team["price-lists"], quote.price_list_code), currency: quote.currency, bank: quote.bank ? { ...quote.bank } : null });
    }
    if (!quote && !head && fin.locations) setHead({ location_id: fin.locations[0] ? fin.locations[0].value : null, customer_type: "REGULAR", payment_type: "CASH", remarks: "", contact_name: "", currency: "AED", price_list_id: null, bank: null });
  }, [quote, fromDoc, fin.locations, team, head]);

  if (!head) return null;
  const set = (patch) => setHead({ ...head, ...patch });
  const save = async () => {
    setBusy(true);
    const body = {
      ...(quote ? {} : partyPayload(party)),
      location_id: head.location_id,
      salesperson_employee_id: head.salesperson_employee_id || undefined,
      customer_type: head.customer_type,
      payment_type: head.payment_type,
      contact_name: head.contact_name,
      remarks: head.remarks,
      price_list_id: head.price_list_id || undefined,
      lead_id: preset && preset.lead_id ? preset.lead_id : undefined,
      bank: head.payment_type === "BANK_FINANCE" ? head.bank : null,
      ...dealPayload(deal),
    };
    const res = await act(() => dmsService.saveQuote(quote ? quote.id : null, body));
    setBusy(false);
    if (res) {
      onSaved(res.data && res.data.id ? res.data.id : quote.id);
      onHide();
    }
  };
  const priceList = (team["price-lists"] || []).find((o) => o.value === head.price_list_id);
  return (
    <Panel
      title={quote ? `Edit quotation ${quote.quote_number}` : "New quotation"}
      onHide={() => !busy && onHide()}
      width={1100}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={busy}>
            Save draft
          </PrimaryButton>
        </>
      }
    >
      {!quote ? (
        <Section title="Customer">
          <PartyPicker value={party} onChange={setParty} />
        </Section>
      ) : (
        <div style={{ ...hint, marginBottom: 12 }}>Customer: {quote.customer_name}</div>
      )}
      <Section title="Deal">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 12px" }}>
          <Field label="Location" required htmlFor="q-loc">
            <AppDropdown id="q-loc" value={head.location_id} options={fin.locations || []} onChange={(e) => set({ location_id: e.value })} />
          </Field>
          <Field label="Sales consultant" htmlFor="q-sp" hint="Blank: you">
            <AppDropdown id="q-sp" value={head.salesperson_employee_id} options={(team.salespersons || []).map((s) => ({ label: s.row.employee_name, value: s.row.employee_id }))} onChange={(e) => set({ salesperson_employee_id: e.value })} placeholder="Me" />
          </Field>
          <Field label="Customer type" htmlFor="q-ct">
            <select id="q-ct" className="form-select" value={head.customer_type} onChange={(e) => set({ customer_type: e.target.value })}>
              <option value="REGULAR">Regular</option>
              <option value="WALKIN">Walk-in</option>
            </select>
          </Field>
          <Field label="Price list" htmlFor="q-pl" hint="Blank: the business unit's default">
            <AppDropdown id="q-pl" value={head.price_list_id} options={team["price-lists"] || []} onChange={(e) => set({ price_list_id: e.value, currency: ((team["price-lists"] || []).find((o) => o.value === e.value) || {}).row?.currency || head.currency })} placeholder="Default" />
          </Field>
          <Field label="Payment" htmlFor="q-pay">
            <AppDropdown id="q-pay" value={head.payment_type} options={payTypes} onChange={(e) => set({ payment_type: e.value })} />
          </Field>
          <Field label="Contact person" htmlFor="q-cn">
            <input id="q-cn" className="form-control" style={inputStyle} value={head.contact_name} onChange={(e) => set({ contact_name: e.target.value })} />
          </Field>
        </div>
        <DealEditor value={deal} onChange={setDeal} context={{ price_list_id: head.price_list_id, currency: priceList ? priceList.row.currency : head.currency }} />
      </Section>
      {head.payment_type === "BANK_FINANCE" ? (
        <Section title="Bank finance">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0 12px" }}>
            <Field label="Bank" htmlFor="b-bank">
              <AppDropdown id="b-bank" value={(head.bank || {}).bank_code || null} options={banks} onChange={(e) => set({ bank: { ...(head.bank || {}), bank_code: e.value } })} placeholder={banks.length ? "Bank" : "Add banks under Setup → Lists"} />
            </Field>
            {[
              ["branch", "Branch"],
              ["contact_name", "Bank contact"],
              ["contact_phone", "Contact phone"],
              ["down_payment", "Down payment", "number"],
              ["finance_amount", "Amount to finance", "number"],
              ["tenure_months", "Tenure (months)", "number"],
            ].map(([k, l, t]) => (
              <Field key={k} label={l} htmlFor={`b-${k}`}>
                <input id={`b-${k}`} type={t || "text"} className="form-control" style={inputStyle} value={(head.bank || {})[k] ?? ""} onChange={(e) => set({ bank: { ...(head.bank || {}), [k]: e.target.value } })} />
              </Field>
            ))}
          </div>
        </Section>
      ) : null}
      <Field label="Remarks" htmlFor="q-rem">
        <textarea id="q-rem" className="form-control" rows={2} value={head.remarks} onChange={(e) => set({ remarks: e.target.value })} />
      </Field>
    </Panel>
  );
}

/* ------------------------------ view ------------------------------ */

function QuoteView({ id, onHide, onChanged, onOpen }) {
  const navigate = useNavigate();
  const { data: q, loading, error, reload } = useLoad(() => dmsService.getQuote(id), [id]);
  const modes = useLookup("FOLLOWUP_MODE");
  const lost = useLookup("LOST_REASON");
  const [form, setForm] = useState(null); // followup | extend | lost | edit
  const refresh = () => {
    reload();
    onChanged();
  };
  const doAct = (action, data) => run(() => dmsService.quoteAction(id, action, data), refresh);
  const actions = q
    ? [
        q.status === "DRAFT" && ["Edit", () => setForm("edit")],
        q.status === "DRAFT" && ["Submit", () => doAct("submit")],
        q.status === "APPROVED" && ["Send to customer", () => doAct("send")],
        ["APPROVED", "PENDING_WITH_CUSTOMER"].includes(q.status) && [
          "Accept",
          () =>
            run(
              () => dmsService.quoteAction(id, "accept", {}),
              (res) => {
                refresh();
                if (window.confirm(`Sales order ${res.data.so_number} created. Open it?`)) navigate(`/dms/orders?id=${encodeURIComponent(res.data.so_id)}`);
              }
            ),
        ],
        ["DRAFT", "APPROVED", "PENDING_WITH_CUSTOMER", "EXPIRED"].includes(q.status) && ["Follow up", () => setForm("followup")],
        ["APPROVED", "PENDING_WITH_CUSTOMER", "EXPIRED"].includes(q.status) && ["Extend validity", () => setForm("extend")],
        ["APPROVED", "PENDING_WITH_CUSTOMER", "EXPIRED", "LOST"].includes(q.status) && [
          "Revise",
          () =>
            run(
              () => dmsService.quoteAction(id, "revise"),
              (res) => {
                onChanged();
                onOpen(res.data.id);
              }
            ),
        ],
        ["PENDING_WITH_CUSTOMER", "EXPIRED"].includes(q.status) && ["Lost", () => setForm("lost")],
        ["DRAFT", "APPROVED", "PENDING_WITH_CUSTOMER"].includes(q.status) && ["Cancel", () => window.confirm("Cancel this quotation?") && doAct("cancel")],
      ].filter(Boolean)
    : [];
  return (
    <Panel title={q ? `Quotation ${q.quote_number}${q.revision ? ` rev ${q.revision}` : ""}` : "Quotation"} onHide={onHide} width={1100}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {q ? (
          <>
            <DocHead title="" number={`${q.customer_name}`} status={q.status} sub={`${q.location_name || ""} · ${q.salesperson_name || ""} · ${formatDate(q.quote_date)} · valid until ${formatDate(q.valid_until)}${q.won_so_number ? ` · order ${q.won_so_number}` : ""}`}>
              {actions.map(([label, fn], i) => (
                <button key={label} type="button" className={`btn ${i === 0 ? "Button primaryBackground text-white" : "btn-outline-secondary"} btn-sm`} onClick={fn}>
                  {label}
                </button>
              ))}
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => dmsService.quotePdf(id, `${q.quote_number}.pdf`)}>
                Customer PDF
              </button>
              {q.bank ? (
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => dmsService.quotePdf(id, `${q.quote_number}-bank.pdf`, true)}>
                  Bank PDF
                </button>
              ) : null}
            </DocHead>
            <Section title="Vehicles">
              <DataTable
                rows={q.lines}
                minWidth={760}
                columns={[
                  { label: "Vehicle", render: (l) => <div><b>{l.make_name} {l.model_name}</b> {l.variant_name} {l.model_year}<div style={hint}>{[l.ext_colour_name, l.int_colour_name].filter(Boolean).join(" / ")}{l.vin ? ` · VIN ${l.vin}` : ""}</div></div> },
                  { label: "List", num: true, render: (l) => fmtMoney(l.list_price) },
                  { label: "Campaign", num: true, render: (l) => (Number(l.campaign_discount) ? <span title={l.campaign_name}>{fmtMoney(l.campaign_discount)}</span> : "—") },
                  { label: "Discount", num: true, render: (l) => (Number(l.discount_amount) ? fmtMoney(l.discount_amount) : "—") },
                  { label: "Net", num: true, render: (l) => fmtMoney(l.net_amount) },
                  { label: "VAT", num: true, render: (l) => fmtMoney(l.tax_amount) },
                  { label: "Total", num: true, render: (l) => fmtMoney(l.line_total) },
                ]}
              />
              {q.charges.length ? (
                <div className="mt-3">
                  <div style={{ fontWeight: 600, fontSize: 14 }}>Services</div>
                  {q.charges.map((c) => (
                    <div key={c.id} className="d-flex justify-content-between" style={{ fontSize: 14 }}>
                      <span>{c.description}</span>
                      <span>{c.is_complimentary ? "Complimentary" : `${fmtMoney(c.amount)} + VAT ${fmtMoney(c.tax_amount)}`}</span>
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="mt-3">
                <Totals doc={q} />
              </div>
            </Section>
            <div className="row">
              <div className="col-md-6">
                <Section title="Follow-ups">
                  {q.followups.length ? (
                    q.followups.map((f) => (
                      <div key={f.id} style={{ fontSize: 14, marginBottom: 8 }}>
                        <b>{f.mode}</b> · {formatDateTime(f.followup_date)}
                        <div>{f.outcome}</div>
                        {f.next_followup_date ? <div style={hint}>Next: {formatDate(f.next_followup_date)}</div> : null}
                      </div>
                    ))
                  ) : (
                    <div style={hint}>None yet.</div>
                  )}
                </Section>
                {q.revisions.length > 1 ? (
                  <Section title="Revisions">
                    {q.revisions.map((r) => (
                      <div key={r.revision} className="d-flex justify-content-between" style={{ fontSize: 14 }}>
                        <button type="button" style={linkBtn} onClick={() => onOpen(r.id)}>
                          Revision {r.revision}
                        </button>
                        <span>
                          <DmsBadge status={r.status} /> {fmtMoney(r.total)}
                        </span>
                      </div>
                    ))}
                  </Section>
                ) : null}
              </div>
              <div className="col-md-6">
                <Section title="Action history">
                  <ActionHistory actions={q.actions} />
                </Section>
              </div>
            </div>
          </>
        ) : null}
      </LoadState>
      {form === "edit" && q ? <QuoteEditor quote={q} onHide={() => setForm(null)} onSaved={refresh} /> : null}
      <FormDrawer
        show={form === "followup"}
        title="Follow up"
        initial={{ mode: "CALL" }}
        onHide={() => setForm(null)}
        onDone={refresh}
        onSave={(clean) => dmsService.quoteAction(id, "followups", clean)}
        fields={[
          { key: "mode", label: "How", type: "select", required: true, options: modes },
          { key: "outcome", label: "What the customer said", type: "textarea", required: true, max: 500 },
          { key: "next_followup_date", label: "Next follow-up", type: "date" },
        ]}
      />
      <FormDrawer
        show={form === "extend"}
        title="Extend validity"
        initial={{ valid_until: todayYmd() }}
        onHide={() => setForm(null)}
        onDone={refresh}
        onSave={(clean) => dmsService.quoteAction(id, "extend", clean)}
        fields={[{ key: "valid_until", label: "Valid until", type: "date", required: true }]}
      />
      <FormDrawer
        show={form === "lost"}
        title="Mark as lost"
        initial={{}}
        onHide={() => setForm(null)}
        onDone={refresh}
        onSave={(clean) => dmsService.quoteAction(id, "lost", clean)}
        fields={[
          { key: "lost_reason", label: "Reason", type: "select", required: true, options: lost },
          { key: "remarks", label: "Remarks", type: "textarea", max: 1000 },
        ]}
      />
    </Panel>
  );
}

/* ------------------------------ lists ------------------------------ */

function QuoteList() {
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({ status: "", q: "" });
  const { data, loading, error, reload } = useLoad(() => dmsService.listQuotes({ status: filters.status || undefined, open: filters.status ? undefined : 1, q: filters.q || undefined }), [filters.status, filters.q]);
  const [creating, setCreating] = useState(false);
  const open = params.get("id");
  const leadId = params.get("lead");
  const prospectId = params.get("prospect");
  useEffect(() => {
    if (leadId || prospectId) setCreating(true);
  }, [leadId, prospectId]);
  const setOpen = (id) => setParams(id ? { id } : {});
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <PrimaryButton onClick={() => setCreating(true)}>New quotation</PrimaryButton>
        <div style={{ width: 200 }}>
          <AppDropdown id="qs" value={filters.status} options={STATUS} onChange={(e) => setFilters({ ...filters, status: e.value })} />
        </div>
        <input className="form-control" style={{ ...inputStyle, width: 240 }} placeholder="Number, customer or phone" aria-label="Search quotations" onKeyDown={(e) => e.key === "Enter" && setFilters({ ...filters, q: e.target.value.trim() })} />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No quotations" />}
          columns={[
            { label: "Number", render: (r) => <b>{r.quote_number}{r.revision ? ` rev ${r.revision}` : ""}</b> },
            { label: "Date", render: (r) => formatDate(r.quote_date) },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Consultant", render: (r) => r.salesperson_name },
            { label: "Valid until", render: (r) => formatDate(r.valid_until) },
            { label: "Total", num: true, render: (r) => `${fmtMoney(r.total)} ${r.currency}` },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
      </LoadState>
      {creating ? (
        <QuoteEditor
          preset={leadId || prospectId ? { lead_id: leadId, party: prospectId ? { mode: "prospect", prospect_id: prospectId } : { mode: "new" } } : null}
          onHide={() => {
            setCreating(false);
            if (leadId || prospectId) setParams({});
          }}
          onSaved={(id) => {
            reload();
            setOpen(id);
          }}
        />
      ) : null}
      {open ? <QuoteView id={open} onHide={() => setOpen(null)} onChanged={reload} onOpen={setOpen} /> : null}
    </>
  );
}

function Prospects() {
  const { data, loading, error, reload } = useLoad(() => dmsService.listProspects({}), []);
  const [edit, setEdit] = useState(undefined);
  const sources = useLookup("ENQUIRY_SOURCE");
  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setEdit(null)}>Add prospect</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => r.status === "OPEN" && setEdit(r)}
          empty={<EmptyState title="No prospects" text="Prospects are added here, from an enquiry, or on a new quotation." />}
          columns={[
            { label: "Name", render: (r) => r.display_name },
            { label: "Phone", render: (r) => r.phone },
            { label: "Email", render: (r) => r.email },
            { label: "Source", render: (r) => r.source_code },
            { label: "Status", render: (r) => <DmsBadge status={r.status === "CONVERTED" ? "WON" : r.status} /> },
            { label: "Added", render: (r) => formatDate(r.created_at) },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={edit !== undefined}
        title={edit ? "Edit prospect" : "Add prospect"}
        initial={edit ? { ...edit } : { party_type: "INDIVIDUAL" }}
        onHide={() => setEdit(undefined)}
        onDone={reload}
        onSave={(clean) => dmsService.saveProspect(edit ? edit.id : null, clean)}
        fields={(v) => [
          { key: "party_type", label: "Type", type: "select", required: true, options: [{ label: "Individual", value: "INDIVIDUAL" }, { label: "Company", value: "COMPANY" }] },
          ...(v.party_type === "COMPANY" ? [{ key: "company_name", label: "Company name", required: true, max: 200 }] : [{ key: "first_name", label: "First name", required: true, max: 60 }, { key: "last_name", label: "Last name", max: 60 }]),
          { key: "phone", label: "Phone", required: true, max: 30 },
          { key: "email", label: "Email", max: 150 },
          { key: "trn", label: "Tax number (TRN)", max: 32 },
          { key: "emirates_id", label: "Emirates ID", max: 32 },
          { key: "address", label: "Address", type: "textarea", max: 500 },
          { key: "source_code", label: "Source", type: "select", options: sources },
        ]}
      />
    </>
  );
}

export default function Quotations() {
  const [tab, setTab] = useState("quotes");
  return (
    <div style={pageStyle}>
      <PageHeader title="Quotations" subtitle="Vehicle quotations and the prospects they are for" />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: "quotes", label: "Quotations" },
          { key: "prospects", label: "Prospects" },
        ]}
      />
      {tab === "quotes" ? <QuoteList /> : <Prospects />}
    </div>
  );
}
